import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Settings,
  TrendingUp,
  Activity,
  Clock,
  Filter,
  ChevronDown,
  ChevronUp,
  Eye,
  BarChart3,
  Layers,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIGovernanceDashboard = () => {
  const queryClient = useQueryClient();
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [logsPage, setLogsPage] = useState(1);
  const [logsFilter, setLogsFilter] = useState({
    agent_type: '',
    has_errors: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogDetail, setShowLogDetail] = useState(false);
  const [trendsDays, setTrendsDays] = useState(30);
  const [newRule, setNewRule] = useState({ name: '', prompt: '', scope: 'global', scope_value: '' });
  const [editingRule, setEditingRule] = useState(null);

  // Fetch governance config
  const { data: configData } = useQuery({
    queryKey: ['governance-config'],
    queryFn: async () => {
      const response = await axios.get(`${API}/governance/config`);
      return response.data;
    }
  });

  // Fetch governance rules
  const { data: rulesData, isLoading: rulesLoading } = useQuery({
    queryKey: ['governance-rules'],
    queryFn: async () => {
      const response = await axios.get(`${API}/governance/rules`);
      return response.data;
    }
  });

  const rules = rulesData?.rules || [];

  // Fetch governance metrics
  const { data: metricsData, isLoading: metricsLoading } = useQuery({
    queryKey: ['governance-metrics'],
    queryFn: async () => {
      const response = await axios.get(`${API}/governance/metrics`);
      return response.data;
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Fetch governance logs
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['governance-logs', logsPage, logsFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: logsPage, limit: 20 });
      if (logsFilter.agent_type) params.append('agent_type', logsFilter.agent_type);
      if (logsFilter.has_errors !== '') params.append('has_errors', logsFilter.has_errors);
      
      const response = await axios.get(`${API}/governance/logs?${params}`);
      return response.data;
    }
  });

  // Fetch stats by agent
  const { data: agentStatsData } = useQuery({
    queryKey: ['governance-stats-agent'],
    queryFn: async () => {
      const response = await axios.get(`${API}/governance/stats/by-agent`);
      return response.data;
    }
  });

  // Fetch stats by module
  const { data: moduleStatsData } = useQuery({
    queryKey: ['governance-stats-module'],
    queryFn: async () => {
      const response = await axios.get(`${API}/governance/stats/by-module`);
      return response.data;
    }
  });

  // Fetch trends
  const { data: trendsData } = useQuery({
    queryKey: ['governance-trends', trendsDays],
    queryFn: async () => {
      const response = await axios.get(`${API}/governance/trends?days=${trendsDays}`);
      return response.data;
    }
  });

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: async (config) => {
      const response = await axios.put(`${API}/governance/config`, config);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['governance-config']);
      toast.success('Configuración actualizada');
    },
    onError: (error) => {
      toast.error(`Error: ${error.response?.data?.detail || error.message}`);
    }
  });

  // Create rule mutation
  const createRuleMutation = useMutation({
    mutationFn: async (rule) => {
      const response = await axios.post(`${API}/governance/rules`, rule);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['governance-rules']);
      setNewRule({ name: '', prompt: '', scope: 'global', scope_value: '' });
      toast.success('Regla creada');
    },
    onError: (error) => {
      toast.error(`Error: ${error.response?.data?.detail || error.message}`);
    }
  });

  // Update rule mutation
  const updateRuleMutation = useMutation({
    mutationFn: async ({ id, rule }) => {
      const response = await axios.put(`${API}/governance/rules/${id}`, rule);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['governance-rules']);
      setEditingRule(null);
      toast.success('Regla actualizada');
    },
    onError: (error) => {
      toast.error(`Error: ${error.response?.data?.detail || error.message}`);
    }
  });

  // Delete rule mutation
  const deleteRuleMutation = useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(`${API}/governance/rules/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['governance-rules']);
      toast.success('Regla eliminada');
    },
    onError: (error) => {
      toast.error(`Error: ${error.response?.data?.detail || error.message}`);
    }
  });

  // Toggle rule active status
  const toggleRuleMutation = useMutation({
    mutationFn: async ({ id, is_active }) => {
      const response = await axios.patch(`${API}/governance/rules/${id}/toggle`, { is_active });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['governance-rules']);
    }
  });

  const config = configData?.config || {};
  const metrics = metricsData?.metrics || {};
  const logs = logsData?.logs || [];
  const agentStats = agentStatsData?.stats || [];
  const moduleStats = moduleStatsData?.stats || [];
  const trends = trendsData?.trends || [];

  const handleConfigChange = (key, value) => {
    updateConfigMutation.mutate({ [key]: value });
  };

  const getStatusColor = (value, threshold, inverse = false) => {
    if (inverse) {
      if (value <= threshold * 0.5) return 'text-green-500';
      if (value <= threshold) return 'text-yellow-500';
      return 'text-red-500';
    }
    if (value >= threshold * 1.2) return 'text-green-500';
    if (value >= threshold) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getConfidenceBadge = (score) => {
    if (score >= 80) return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">{score}%</Badge>;
    if (score >= 60) return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">{score}%</Badge>;
    return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">{score}%</Badge>;
  };

  const formatLatency = (ms) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <div className="space-y-6" data-testid="ai-governance-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/30">
            <Shield className="h-6 w-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Gobernanza de IA</h2>
            <p className="text-slate-400 text-sm">Monitoreo y control de calidad de salidas LLM</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            queryClient.invalidateQueries(['governance-metrics']);
            queryClient.invalidateQueries(['governance-logs']);
          }}
          data-testid="refresh-governance-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Tabs Navigation */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="bg-slate-800/50 border border-slate-700">
          <TabsTrigger value="overview" className="data-[state=active]:bg-purple-500/20">
            <Activity className="h-4 w-4 mr-2" />
            Resumen
          </TabsTrigger>
          <TabsTrigger value="logs" className="data-[state=active]:bg-purple-500/20">
            <BarChart3 className="h-4 w-4 mr-2" />
            Logs
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-purple-500/20">
            <TrendingUp className="h-4 w-4 mr-2" />
            Analíticas
          </TabsTrigger>
          <TabsTrigger value="config" className="data-[state=active]:bg-purple-500/20">
            <Settings className="h-4 w-4 mr-2" />
            Configuración
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">
                  Ejecuciones Totales
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">
                  {metricsLoading ? '...' : metrics.total_executions || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">Validaciones procesadas</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  Confianza Promedio
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${getStatusColor(metrics.avg_confidence_score || 0, 70)}`}>
                  {metricsLoading ? '...' : `${metrics.avg_confidence_score || 0}%`}
                </div>
                <p className="text-xs text-slate-500 mt-1">Score de confiabilidad</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-400" />
                  Tasa de Errores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${getStatusColor(metrics.schema_error_rate || 0, 20, true)}`}>
                  {metricsLoading ? '...' : `${metrics.schema_error_rate || 0}%`}
                </div>
                <p className="text-xs text-slate-500 mt-1">Errores de schema</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-blue-400" />
                  Tasa de Reparación
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-400">
                  {metricsLoading ? '...' : `${metrics.repair_rate || 0}%`}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {metrics.repair_success_rate || 0}% exitosas
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">
                  Inconsistencias de Datos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getStatusColor(metrics.data_inconsistency_rate || 0, 15, true)}`}>
                  {metrics.data_inconsistency_rate || 0}%
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, metrics.data_inconsistency_rate || 0)}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">
                  Latencia de Validación
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  {formatLatency(metrics.avg_validation_latency || 0)}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Total: {formatLatency(metrics.avg_total_latency || 0)}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">
                  Estado del Pipeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${config.enable_repair_loop ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`} />
                  <span className="text-white font-medium">
                    {config.enable_repair_loop ? 'Repair Loop Activo' : 'Repair Loop Inactivo'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Modo: {config.strict_mode ? 'Estricto' : 'Permisivo'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white">
                Actividad Reciente
              </CardTitle>
              <CardDescription>Últimas validaciones procesadas</CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No hay registros de gobernanza aún</p>
                  <p className="text-sm mt-1">Los logs aparecerán cuando se ejecuten agentes</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Agente</TableHead>
                      <TableHead className="text-slate-400">Módulo</TableHead>
                      <TableHead className="text-slate-400">Estado</TableHead>
                      <TableHead className="text-slate-400">Confianza</TableHead>
                      <TableHead className="text-slate-400">Fecha</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.slice(0, 5).map((log) => (
                      <TableRow 
                        key={log.id} 
                        className="border-slate-700 hover:bg-slate-700/30 cursor-pointer"
                        onClick={() => {
                          setSelectedLog(log);
                          setShowLogDetail(true);
                        }}
                      >
                        <TableCell className="font-medium text-white">
                          {log.agent_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-slate-300">
                            {log.module_code}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {log.had_schema_errors || log.had_data_inconsistencies ? (
                            <Badge className="bg-red-500/20 text-red-400">
                              <XCircle className="h-3 w-3 mr-1" />
                              Errores
                            </Badge>
                          ) : (
                            <Badge className="bg-green-500/20 text-green-400">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Válido
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {getConfidenceBadge(Math.round((log.final_confidence_score || 0) * 100))}
                        </TableCell>
                        <TableCell className="text-slate-400 text-sm">
                          {new Date(log.created_at).toLocaleString('es-ES')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOGS TAB */}
        <TabsContent value="logs" className="space-y-6 mt-6">
          {/* Filters */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-400">Filtros:</span>
                </div>
                
                <Select 
                  value={logsFilter.agent_type || 'all'} 
                  onValueChange={(v) => setLogsFilter({...logsFilter, agent_type: v === 'all' ? '' : v})}
                >
                  <SelectTrigger className="w-40 bg-slate-700 border-slate-600">
                    <SelectValue placeholder="Tipo de Agente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="functional">Funcional</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="c_level">C-Level</SelectItem>
                  </SelectContent>
                </Select>

                <Select 
                  value={logsFilter.has_errors || 'all'} 
                  onValueChange={(v) => setLogsFilter({...logsFilter, has_errors: v === 'all' ? '' : v})}
                >
                  <SelectTrigger className="w-40 bg-slate-700 border-slate-600">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="true">Con Errores</SelectItem>
                    <SelectItem value="false">Sin Errores</SelectItem>
                  </SelectContent>
                </Select>

                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setLogsFilter({ agent_type: '', has_errors: '' })}
                >
                  Limpiar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Logs Table */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              {logsLoading ? (
                <div className="text-center py-8 text-slate-400">Cargando logs...</div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No hay logs que coincidan con los filtros</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Fecha</TableHead>
                        <TableHead className="text-slate-400">Agente</TableHead>
                        <TableHead className="text-slate-400">Tipo</TableHead>
                        <TableHead className="text-slate-400">Módulo</TableHead>
                        <TableHead className="text-slate-400">Schema</TableHead>
                        <TableHead className="text-slate-400">Datos</TableHead>
                        <TableHead className="text-slate-400">Reparación</TableHead>
                        <TableHead className="text-slate-400">Confianza</TableHead>
                        <TableHead className="text-slate-400">Latencia</TableHead>
                        <TableHead className="text-slate-400"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log) => (
                        <TableRow key={log.id} className="border-slate-700 hover:bg-slate-700/30">
                          <TableCell className="text-sm text-slate-300">
                            {new Date(log.created_at).toLocaleString('es-ES', { 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell className="font-medium text-white max-w-[150px] truncate">
                            {log.agent_name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`
                              ${log.agent_type === 'functional' ? 'border-blue-500/50 text-blue-400' : ''}
                              ${log.agent_type === 'premium' ? 'border-purple-500/50 text-purple-400' : ''}
                              ${log.agent_type === 'c_level' ? 'border-amber-500/50 text-amber-400' : ''}
                            `}>
                              {log.agent_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300">{log.module_code}</TableCell>
                          <TableCell>
                            {log.had_schema_errors ? (
                              <span className="text-red-400 flex items-center gap-1">
                                <XCircle className="h-4 w-4" />
                                {log.schema_error_count}
                              </span>
                            ) : (
                              <span className="text-green-400">
                                <CheckCircle className="h-4 w-4" />
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {log.had_data_inconsistencies ? (
                              <span className="text-yellow-400 flex items-center gap-1">
                                <AlertTriangle className="h-4 w-4" />
                                {log.inconsistency_count}
                              </span>
                            ) : (
                              <span className="text-green-400">
                                <CheckCircle className="h-4 w-4" />
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {log.repair_used ? (
                              <Badge className={`${log.repair_successful ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                {log.repair_successful ? 'Exitosa' : 'Fallida'}
                              </Badge>
                            ) : (
                              <span className="text-slate-500">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {getConfidenceBadge(Math.round((log.final_confidence_score || 0) * 100))}
                          </TableCell>
                          <TableCell className="text-slate-400 text-sm">
                            {formatLatency(log.total_latency_ms || 0)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedLog(log);
                                setShowLogDetail(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                    <p className="text-sm text-slate-400">
                      Página {logsData?.page || 1} de {logsData?.pages || 1} ({logsData?.total || 0} total)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLogsPage(p => Math.max(1, p - 1))}
                        disabled={logsPage <= 1}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLogsPage(p => p + 1)}
                        disabled={logsPage >= (logsData?.pages || 1)}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ANALYTICS TAB */}
        <TabsContent value="analytics" className="space-y-6 mt-6">
          {/* Stats by Agent */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Layers className="h-5 w-5 text-blue-400" />
                Rendimiento por Agente
              </CardTitle>
            </CardHeader>
            <CardContent>
              {agentStats.length === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  No hay datos de agentes aún
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Agente</TableHead>
                      <TableHead className="text-slate-400">Tipo</TableHead>
                      <TableHead className="text-slate-400 text-right">Ejecuciones</TableHead>
                      <TableHead className="text-slate-400 text-right">Tasa Error</TableHead>
                      <TableHead className="text-slate-400 text-right">Tasa Reparación</TableHead>
                      <TableHead className="text-slate-400 text-right">Confianza Avg</TableHead>
                      <TableHead className="text-slate-400 text-right">Latencia Avg</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agentStats.map((stat, idx) => (
                      <TableRow key={idx} className="border-slate-700">
                        <TableCell className="font-medium text-white">{stat.agent_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{stat.agent_type}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-slate-300">{stat.total_executions}</TableCell>
                        <TableCell className={`text-right ${getStatusColor(stat.error_rate, 20, true)}`}>
                          {stat.error_rate}%
                        </TableCell>
                        <TableCell className="text-right text-blue-400">{stat.repair_rate}%</TableCell>
                        <TableCell className={`text-right ${getStatusColor(stat.avg_confidence, 70)}`}>
                          {stat.avg_confidence}%
                        </TableCell>
                        <TableCell className="text-right text-slate-400">
                          {formatLatency(stat.avg_latency_ms)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Stats by Module */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-400" />
                Rendimiento por Módulo
              </CardTitle>
            </CardHeader>
            <CardContent>
              {moduleStats.length === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  No hay datos de módulos aún
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {moduleStats.map((stat, idx) => (
                    <Card key={idx} className="bg-slate-700/50 border-slate-600">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-md font-medium text-white uppercase">
                          {stat.module_code}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Ejecuciones:</span>
                          <span className="text-white font-medium">{stat.total_executions}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Errores Schema:</span>
                          <span className={getStatusColor(stat.schema_error_rate, 20, true)}>
                            {stat.schema_error_rate}%
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Inconsistencias:</span>
                          <span className={getStatusColor(stat.inconsistency_rate, 15, true)}>
                            {stat.inconsistency_rate}%
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Confianza:</span>
                          <span className={getStatusColor(stat.avg_confidence, 70)}>
                            {stat.avg_confidence}%
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trends */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                  Tendencias
                </CardTitle>
                <Select value={String(trendsDays)} onValueChange={(v) => setTrendsDays(Number(v))}>
                  <SelectTrigger className="w-32 bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 días</SelectItem>
                    <SelectItem value="14">14 días</SelectItem>
                    <SelectItem value="30">30 días</SelectItem>
                    <SelectItem value="90">90 días</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {trends.length === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  No hay datos de tendencias aún
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4 text-sm text-slate-400 pb-2 border-b border-slate-700">
                    <span>Fecha</span>
                    <span className="text-right">Ejecuciones</span>
                    <span className="text-right">Tasa Error</span>
                    <span className="text-right">Confianza</span>
                  </div>
                  {trends.slice(-10).map((trend, idx) => (
                    <div key={idx} className="grid grid-cols-4 gap-4 text-sm">
                      <span className="text-slate-300">{trend.date}</span>
                      <span className="text-right text-white">{trend.executions}</span>
                      <span className={`text-right ${getStatusColor(trend.error_rate, 20, true)}`}>
                        {trend.error_rate}%
                      </span>
                      <span className={`text-right ${getStatusColor(trend.avg_confidence, 70)}`}>
                        {trend.avg_confidence}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONFIG TAB */}
        <TabsContent value="config" className="space-y-6 mt-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white">
                Configuración del Pipeline
              </CardTitle>
              <CardDescription>
                Ajusta el comportamiento del módulo de gobernanza de IA
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Repair Loop */}
              <div className="flex items-center justify-between py-4 border-b border-slate-700">
                <div>
                  <h4 className="font-medium text-white">Repair Loop</h4>
                  <p className="text-sm text-slate-400">
                    Habilitar corrección automática de errores mediante segundo llamado LLM
                  </p>
                </div>
                <Switch
                  checked={config.enable_repair_loop || false}
                  onCheckedChange={(checked) => handleConfigChange('enable_repair_loop', checked)}
                  data-testid="toggle-repair-loop"
                />
              </div>

              {/* Enable Data Validation */}
              <div className="flex items-center justify-between py-4 border-b border-slate-700">
                <div>
                  <h4 className="font-medium text-white">Validación de Datos</h4>
                  <p className="text-sm text-slate-400">
                    Verificar consistencia de datos contra fuente original y benchmarks
                  </p>
                </div>
                <Switch
                  checked={config.enable_data_validation || false}
                  onCheckedChange={(checked) => handleConfigChange('enable_data_validation', checked)}
                  data-testid="toggle-data-validation"
                />
              </div>

              {/* Strict Mode */}
              <div className="flex items-center justify-between py-4 border-b border-slate-700">
                <div>
                  <h4 className="font-medium text-white">Modo Estricto</h4>
                  <p className="text-sm text-slate-400">
                    Rechazar outputs que no pasen todas las validaciones
                  </p>
                </div>
                <Switch
                  checked={config.strict_mode || false}
                  onCheckedChange={(checked) => handleConfigChange('strict_mode', checked)}
                  data-testid="toggle-strict-mode"
                />
              </div>

              {/* Log to Database */}
              <div className="flex items-center justify-between py-4 border-b border-slate-700">
                <div>
                  <h4 className="font-medium text-white">Logging a BD</h4>
                  <p className="text-sm text-slate-400">
                    Guardar logs detallados de cada validación en MongoDB
                  </p>
                </div>
                <Switch
                  checked={config.log_to_database !== false}
                  onCheckedChange={(checked) => handleConfigChange('log_to_database', checked)}
                  data-testid="toggle-logging"
                />
              </div>

              {/* Max Repair Attempts */}
              <div className="py-4 border-b border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-white">Intentos de Reparación</h4>
                    <p className="text-sm text-slate-400">
                      Número máximo de intentos para corregir errores
                    </p>
                  </div>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    value={config.max_repair_attempts || 2}
                    onChange={(e) => handleConfigChange('max_repair_attempts', parseInt(e.target.value))}
                    className="w-20 bg-slate-700 border-slate-600"
                    data-testid="input-max-retries"
                  />
                </div>
              </div>

              {/* Confidence Threshold */}
              <div className="py-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-white">Umbral de Confianza</h4>
                    <p className="text-sm text-slate-400">
                      Score mínimo aceptable (0.0 - 1.0)
                    </p>
                  </div>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={config.confidence_threshold || 0.7}
                    onChange={(e) => handleConfigChange('confidence_threshold', parseFloat(e.target.value))}
                    className="w-20 bg-slate-700 border-slate-600"
                    data-testid="input-confidence-threshold"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Governance Rules */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Shield className="h-5 w-5 text-purple-400" />
                Reglas de Gobernanza
              </CardTitle>
              <CardDescription>
                Define reglas que todos los outputs deben cumplir. Escribe instrucciones en forma de prompt.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* New Rule Form */}
              <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                <h4 className="font-medium text-white mb-4">Nueva Regla</h4>
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Nombre de la regla</label>
                    <Input
                      placeholder="Ej: Prohibir consejos financieros directos"
                      value={newRule.name}
                      onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                      className="bg-slate-700 border-slate-600"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Prompt / Instrucción</label>
                    <textarea
                      placeholder="Escribe la regla como instrucción para el LLM. Ej: 'El output NO debe contener recomendaciones de inversión específicas. Si detectas consejos financieros directos, marca como violación.'"
                      value={newRule.prompt}
                      onChange={(e) => setNewRule({ ...newRule, prompt: e.target.value })}
                      className="w-full h-24 px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 resize-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-400 mb-1 block">Alcance</label>
                      <Select value={newRule.scope} onValueChange={(v) => setNewRule({ ...newRule, scope: v, scope_value: '' })}>
                        <SelectTrigger className="bg-slate-700 border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="global">Global (Todos los agentes)</SelectItem>
                          <SelectItem value="module">Por Módulo</SelectItem>
                          <SelectItem value="agent">Por Agente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {newRule.scope !== 'global' && (
                      <div>
                        <label className="text-sm text-slate-400 mb-1 block">
                          {newRule.scope === 'module' ? 'Módulo' : 'ID/Nombre del Agente'}
                        </label>
                        {newRule.scope === 'module' ? (
                          <Select value={newRule.scope_value} onValueChange={(v) => setNewRule({ ...newRule, scope_value: v })}>
                            <SelectTrigger className="bg-slate-700 border-slate-600">
                              <SelectValue placeholder="Seleccionar módulo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="revenue">Revenue</SelectItem>
                              <SelectItem value="operations">Operations</SelectItem>
                              <SelectItem value="finance">Finance</SelectItem>
                              <SelectItem value="hr">HR</SelectItem>
                              <SelectItem value="supply_chain">Supply Chain</SelectItem>
                              <SelectItem value="logistics">Logistics</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            placeholder="Ej: FUNC-RVM-001"
                            value={newRule.scope_value}
                            onChange={(e) => setNewRule({ ...newRule, scope_value: e.target.value })}
                            className="bg-slate-700 border-slate-600"
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => createRuleMutation.mutate(newRule)}
                    disabled={!newRule.name || !newRule.prompt || createRuleMutation.isLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {createRuleMutation.isLoading ? 'Creando...' : 'Crear Regla'}
                  </Button>
                </div>
              </div>

              {/* Rules List */}
              <div className="space-y-3">
                <h4 className="font-medium text-white">Reglas Activas ({rules.length})</h4>
                {rulesLoading ? (
                  <p className="text-slate-400">Cargando reglas...</p>
                ) : rules.length === 0 ? (
                  <p className="text-slate-400 text-sm">No hay reglas definidas. Crea una para comenzar.</p>
                ) : (
                  rules.map((rule) => (
                    <div key={rule.id} className={`p-4 rounded-lg border ${rule.is_active ? 'bg-slate-900/50 border-slate-600' : 'bg-slate-900/20 border-slate-700 opacity-60'}`}>
                      {editingRule?.id === rule.id ? (
                        <div className="space-y-3">
                          <Input
                            value={editingRule.name}
                            onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })}
                            className="bg-slate-700 border-slate-600"
                          />
                          <textarea
                            value={editingRule.prompt}
                            onChange={(e) => setEditingRule({ ...editingRule, prompt: e.target.value })}
                            className="w-full h-20 px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white resize-none"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => updateRuleMutation.mutate({ id: rule.id, rule: editingRule })} className="bg-green-600 hover:bg-green-700">Guardar</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingRule(null)}>Cancelar</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h5 className="font-medium text-white">{rule.name}</h5>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className={`text-xs ${rule.scope === 'global' ? 'border-purple-500 text-purple-400' : rule.scope === 'module' ? 'border-blue-500 text-blue-400' : 'border-green-500 text-green-400'}`}>
                                  {rule.scope === 'global' ? 'Global' : rule.scope === 'module' ? `Módulo: ${rule.scope_value}` : `Agente: ${rule.scope_value}`}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={rule.is_active}
                                onCheckedChange={(checked) => toggleRuleMutation.mutate({ id: rule.id, is_active: checked })}
                              />
                              <Button size="sm" variant="ghost" onClick={() => setEditingRule(rule)} className="text-slate-400 hover:text-white">
                                <Settings className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteRuleMutation.mutate(rule.id)} className="text-red-400 hover:text-red-300">
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-sm text-slate-400 whitespace-pre-wrap">{rule.prompt}</p>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Log Detail Dialog */}
      <Dialog open={showLogDetail} onOpenChange={setShowLogDetail}>
        <DialogContent className="max-w-3xl bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-400" />
              Detalle de Log de Gobernanza
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400">Agente</label>
                  <p className="text-white font-medium">{selectedLog.agent_name}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Tipo</label>
                  <p className="text-white">{selectedLog.agent_type}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Módulo</label>
                  <p className="text-white">{selectedLog.module_code}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Fecha</label>
                  <p className="text-white">{new Date(selectedLog.created_at).toLocaleString('es-ES')}</p>
                </div>
              </div>

              {/* Validation Results */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="font-medium text-white mb-3">Resultados de Validación</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    {selectedLog.had_schema_errors ? (
                      <XCircle className="h-4 w-4 text-red-400" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-green-400" />
                    )}
                    <span className="text-slate-300">
                      Errores Schema: {selectedLog.schema_error_count || 0}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedLog.had_data_inconsistencies ? (
                      <AlertTriangle className="h-4 w-4 text-yellow-400" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-green-400" />
                    )}
                    <span className="text-slate-300">
                      Inconsistencias: {selectedLog.inconsistency_count || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Repair Info */}
              {selectedLog.repair_used && (
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h4 className="font-medium text-white mb-3">Reparación</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 text-blue-400" />
                      <span className="text-slate-300">
                        Intentos: {selectedLog.retries_count}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedLog.repair_successful ? (
                        <CheckCircle className="h-4 w-4 text-green-400" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-400" />
                      )}
                      <span className="text-slate-300">
                        {selectedLog.repair_successful ? 'Reparación Exitosa' : 'Reparación Fallida'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Scores & Latency */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <p className="text-sm text-slate-400">Confianza Inicial</p>
                  <p className="text-2xl font-bold text-white">
                    {Math.round((selectedLog.initial_confidence_score || 0) * 100)}%
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <p className="text-sm text-slate-400">Confianza Final</p>
                  <p className="text-2xl font-bold text-green-400">
                    {Math.round((selectedLog.final_confidence_score || 0) * 100)}%
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <p className="text-sm text-slate-400">Latencia Total</p>
                  <p className="text-2xl font-bold text-blue-400">
                    {formatLatency(selectedLog.total_latency_ms || 0)}
                  </p>
                </div>
              </div>

              {/* Schema Errors */}
              {selectedLog.schema_errors && selectedLog.schema_errors.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <h4 className="font-medium text-red-400 mb-2">Errores de Schema</h4>
                  <ul className="space-y-1 text-sm text-slate-300">
                    {selectedLog.schema_errors.map((err, idx) => (
                      <li key={idx}>
                        <span className="text-red-400">{err.field}:</span> {err.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Data Inconsistencies */}
              {selectedLog.data_inconsistencies && selectedLog.data_inconsistencies.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-400 mb-2">Inconsistencias de Datos</h4>
                  <ul className="space-y-1 text-sm text-slate-300">
                    {selectedLog.data_inconsistencies.map((inc, idx) => (
                      <li key={idx}>
                        <span className="text-yellow-400">{inc.field}:</span> {inc.message}
                        {inc.expected && <span className="text-slate-500"> (esperado: {String(inc.expected)})</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Output Preview */}
              {selectedLog.raw_output_preview && (
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h4 className="font-medium text-white mb-2">Preview del Output</h4>
                  <pre className="text-xs text-slate-400 bg-slate-800 p-3 rounded overflow-x-auto">
                    {selectedLog.raw_output_preview}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIGovernanceDashboard;
